import './styles/reset.css';
import './styles/main.css';

import { configuredSocialUrls } from './components/social';
import { SITE_CONFIG } from './config/site-config';
import { initAnalytics } from './features/analytics/consent-banner';
import { recordCtaClick } from './features/leads/lead-context';
import { renderChrome } from './features/navigation/chrome';
import { parseHashLocation } from './features/router/location';
import { renderRoute, rerenderRoute, scrollToAnchor } from './features/router/router';
import { getCurrentPath } from './features/router/router-state';
import { initialTheme, initThemeSwitcher, setThemeChangeListener } from './features/theme/theme-switcher';
import { $, prefersReducedMotion, requireElement } from './lib/dom';
import { isRecord } from './lib/guards';
import { logger } from './lib/logger';
import { setThemeClass } from './lib/theme';

/** Keeps the Organization JSON-LD `sameAs` list in sync with the configured social profiles. */
function syncOrganizationSameAs(): void {
  const script = $('#org-ld');
  if (!script?.textContent) return;
  try {
    const data: unknown = JSON.parse(script.textContent);
    if (!isRecord(data)) return;
    script.textContent = JSON.stringify({
      ...data,
      sameAs: configuredSocialUrls(['linkedin', 'youtube', 'instagram']),
    });
  } catch (error) {
    logger.warn('Organization structured data is not valid JSON.', error);
  }
}

function onDocumentClick(event: MouseEvent): void {
  const link = event.target instanceof Element ? event.target.closest('a') : null;
  if (!link) return;
  const href = link.getAttribute('href') ?? '';
  if (link.dataset.cta) recordCtaClick(link, getCurrentPath());
  if (link.hasAttribute('data-skip')) {
    event.preventDefault();
    const main = requireElement('#main');
    main.focus();
    main.scrollIntoView();
    return;
  }
  if (href === '#') {
    event.preventDefault();
    return;
  }
  // Hash mode: clicking a link to the page already shown scrolls instead of re-rendering.
  if (href.startsWith('#/') && href === location.hash) {
    event.preventDefault();
    const anchor = parseHashLocation(href)?.anchor ?? null;
    if (!scrollToAnchor(anchor)) window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }
}

function onHashChange(): void {
  if (SITE_CONFIG.ROUTING === 'path') scrollToAnchor(decodeURIComponent(location.hash.slice(1)));
  else renderRoute();
}

function boot(): void {
  if (!document.getElementById('rt-root')) {
    logger.warn('Page shell (#rt-root) not found; the site script was not started.');
    return;
  }
  const root = document.documentElement;
  root.classList.remove('no-js');
  root.classList.add('rt-site');
  const query = new URLSearchParams(location.search);
  const theme = initialTheme(query);
  if (theme) setThemeClass(theme);

  syncOrganizationSameAs();
  setThemeChangeListener(() => {
    renderChrome();
    rerenderRoute();
  });
  renderChrome();
  initThemeSwitcher(query);
  document.addEventListener('click', onDocumentClick);
  window.addEventListener('hashchange', onHashChange);
  renderRoute({ first: true });
  initAnalytics();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
